import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchWalletPayments, searchWalletUser } from "../modules/wallet/services/walletService";
import "../css/WalletPayment.css";
import "../css/AdManagement.css";
import "../css/WalletCharge.css";
import Layout from "./Layout";

const PAGE_SIZE = 30;

const STATUS_LABEL = {
  PAID: "결제완료",
  PARTIALLY_CANCELED: "부분취소",
  CANCELED: "취소완료",
};

const STATUS_CLASS = {
  PAID: "wp-status-paid",
  PARTIALLY_CANCELED: "wp-status-partial",
  CANCELED: "wp-status-canceled",
};

function formatDateTime(str) {
  if (!str) return "-";
  const d = new Date(str);
  if (isNaN(d)) return str;
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const EMPTY_FILTER = {
  userQuery: "",
  orderId: "",
  status: "",
  fromDate: "",
  toDate: "",
};

function WalletPaymentManagement() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState({ ...EMPTY_FILTER });
  const [tempFilter, setTempFilter] = useState({ ...EMPTY_FILTER });

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const result = await fetchWalletPayments({ ...filter, page, size: PAGE_SIZE });
        if (!cancelled) {
          setList(result.list || []);
          setTotalCount(result.totalCount || 0);
        }
      } catch {
        if (!cancelled) {
          setList([]);
          setTotalCount(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [filter, page]);

  async function handleUserClick(memberNo) {
    try {
      const result = await searchWalletUser(String(memberNo));
      navigate(`/wallet/user/${result.memberNo}`, { state: { prefetched: result } });
    } catch {
      alert('사용자 정보를 찾을 수 없습니다.');
    }
  }

  function handleSearch() {
    setFilter({ ...tempFilter });
    setPage(1);
  }

  function handleReset() {
    setTempFilter({ ...EMPTY_FILTER });
    setFilter({ ...EMPTY_FILTER });
    setPage(1);
  }

  function handleTempChange(e) {
    const { name, value } = e.target;
    setTempFilter((prev) => ({ ...prev, [name]: value }));
  }

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const startIndex = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(page * PAGE_SIZE, totalCount);

  return (
    <Layout>
      <div className="wallet-payment">
        <h4 style={{ fontWeight: 700, color: "#1f2937", margin: 0 }}>결제/차감 관리</h4>

        <div className="wallet-payment-filter">
          <input
            type="text"
            name="userQuery"
            placeholder="사용자 검색"
            value={tempFilter.userQuery}
            onChange={handleTempChange}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            style={{ width: 160 }}
          />
          <input
            type="text"
            name="orderId"
            placeholder="주문ID"
            value={tempFilter.orderId}
            onChange={handleTempChange}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            style={{ width: 120 }}
          />
          <select
            name="status"
            value={tempFilter.status}
            onChange={handleTempChange}
            style={{ width: 130 }}
          >
            <option value="">전체</option>
            <option value="PAID">결제완료</option>
            <option value="PARTIALLY_CANCELED">부분취소</option>
            <option value="CANCELED">취소완료</option>
          </select>
          <input
            type="date"
            name="fromDate"
            className="wallet-date-input"
            value={tempFilter.fromDate}
            onChange={handleTempChange}
          />
          <span style={{ fontSize: "0.85rem", color: "#6b7280" }}>~</span>
          <input
            type="date"
            name="toDate"
            className="wallet-date-input"
            value={tempFilter.toDate}
            onChange={handleTempChange}
          />
          <button className="wallet-filter-action-btn wallet-filter-search" onClick={handleSearch}>
            검색
          </button>
          <button className="wallet-filter-action-btn wallet-filter-reset" onClick={handleReset}>
            초기화
          </button>
        </div>

        <div className="wallet-payment-table-section">
          {loading ? (
            <div className="text-center text-muted py-4">불러오는 중...</div>
          ) : list.length === 0 ? (
            <div className="wallet-payment-empty">조회된 데이터가 없습니다</div>
          ) : (
            <>
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>접수일</th>
                    <th>상품명</th>
                    <th>사용자</th>
                    <th>총결제금액</th>
                    <th>EP 차감</th>
                    <th>CP 차감</th>
                    <th>상태</th>
                    <th>취소</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((item) => (
                    <tr key={item.orderId}>
                      <td>{formatDateTime(item.createdAt)}</td>
                      <td>{item.productName}</td>
                      <td>
                        <button
                          onClick={() => handleUserClick(item.memberNo)}
                          style={{ background: 'none', border: 'none', padding: 0, color: '#6366f1', cursor: 'pointer', fontSize: 'inherit', fontWeight: 600, textDecoration: 'underline' }}
                        >
                          {item.userName}({item.memberNo})
                        </button>
                      </td>
                      <td>{item.totalAmount != null ? item.totalAmount.toLocaleString() + " 원" : "-"}</td>
                      <td>{item.epDeducted != null ? item.epDeducted.toLocaleString() + " P" : "-"}</td>
                      <td>{item.cpDeducted != null ? item.cpDeducted.toLocaleString() + " P" : "-"}</td>
                      <td>
                        <span className={`wp-status-badge ${STATUS_CLASS[item.status] || ""}`}>
                          {STATUS_LABEL[item.status] || item.status}
                        </span>
                      </td>
                      <td>
                        {item.cancelable ? (
                          <button
                            className="wp-cancel-btn"
                            onClick={() => navigate("/wallet/cancel", { state: { highlightId: item.orderId } })}
                          >
                            취소
                          </button>
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="pagination-info">
                <div>총 {totalCount}개 항목 중 {startIndex} - {endIndex}</div>
                <div className="pagination">
                  <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                    이전
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button
                      key={i + 1}
                      className={page === i + 1 ? "active" : ""}
                      onClick={() => setPage(i + 1)}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={endIndex >= totalCount}>
                    다음
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default WalletPaymentManagement;
